import type { CommissionReport } from "./commission-report-calc";
import { DAY_KIND_NB, type PayrollBasisEmployee } from "./overtime-calc";
import type { CommissionNettingResult } from "./commission-netting";
import type { ExtrasSalaryEntry } from "./extras-salary-calc";

/**
 * CSV-formel-injection-vern (OWASP): felt som starter med =/+/-/@ tolkes som
 * formel av Excel/Sheets ved åpning. Alle CSV-eksportene bruker fritekst fra
 * eksterne kilder i navnefeltet (bl.a. Monday-synkede statistnavn,
 * VIB-1283), så et navn som "=HYPERLINK(...)" ville blitt en levende formel
 * uquotet. Prefiks med en apostrof — Excel/Sheets viser da feltet som ren
 * tekst i stedet for å evaluere det. Kjøres FØR semikolon/anførselstegn-
 * quoting under, så et prefikset felt kan fortsatt bli quotet hvis det i
 * tillegg inneholder semikolon/anførselstegn/linjeskift.
 */
function guardFormulaPrefix(value: string): string {
  return /^[=+\-@]/.test(value) ? `'${value}` : value;
}

function csvField(value: string): string {
  const guarded = guardFormulaPrefix(value);
  if (/[";\n]/.test(guarded)) {
    return `"${guarded.replace(/"/g, '""')}"`;
  }
  return guarded;
}

/** Prosent-verdi med opptil 2 desimaler, norsk komma (norsk Excel). */
function pctField(value: number): string {
  return String(Math.round(value * 100) / 100).replace(".", ",");
}

const HEADER =
  "Ansatt;Prosjektkode;Prosjekt;Lukket;GEEK reell (prosjekt);Splitt;Eierandel;GEEK-andel;Sats %;Provisjon";

/**
 * Provisjonsgrunnlag som CSV (VIB-1284): én grunnlagsrad per ansatt ×
 * prosjekt, sum-rad per ansatt og totalsum nederst. Sum-tallene bruker samme
 * avrunding (hele kroner) som rapport-UI-et. Semikolon-separert (norsk Excel).
 * Ingen BOM her — den legges på i downloadCsv slik at serialiseringen er ren
 * testbar tekst.
 */
export function commissionReportToCsv(
  report: CommissionReport,
  deltas?: CommissionNettingResult,
): string {
  const lines = [HEADER];

  for (const emp of report.byEmployee) {
    const details = report.detailByUser[emp.user_id] ?? [];
    for (const d of details) {
      lines.push(
        [
          csvField(emp.full_name),
          csvField(d.project_code ?? ""),
          csvField(d.project_name),
          d.locked_at.slice(0, 10),
          String(Math.round(d.bf_actual)),
          d.split_type,
          d.split_type === "fixed" ? "Fast beløp" : pctField(d.ownership_pct ?? 0),
          String(Math.round(d.bf_share)),
          pctField(d.rate_pct),
          String(Math.round(d.provision)),
        ].join(";"),
      );
    }
    lines.push(
      [
        csvField(`Sum ${emp.full_name}`),
        "",
        `${emp.project_count} prosjekter`,
        "",
        "",
        "",
        "",
        String(Math.round(emp.total_bf_share)),
        "",
        String(Math.round(emp.total_provision)),
      ].join(";"),
    );
  }

  lines.push(
    ["Totalsum", "", "", "", "", "", "", "", "", String(Math.round(report.grandTotal))].join(";"),
  );

  // VIB-1240 (retning B): delta-rader for sene endringer etter lukking —
  // live BF-grunnlag minus utbetalt grunnlag, nettes mot neste kjøring
  // (begge veier: negativ = trekk, positiv = etterbetaling).
  if (deltas && deltas.rows.length > 0) {
    lines.push("");
    lines.push("Netting — sene endringer etter lukking (nettes mot neste kjøring)");
    lines.push("Ansatt;Prosjektkode;Prosjekt;Lukket;GEEK utbetalt grunnlag;GEEK live;;Delta GEEK-andel;Sats %;Delta provisjon");
    for (const d of deltas.rows) {
      lines.push(
        [
          csvField(d.full_name),
          csvField(d.project_code ?? ""),
          csvField(d.project_name),
          d.locked_at.slice(0, 10),
          String(Math.round(d.paidBfKr)),
          String(Math.round(d.liveBfKr)),
          "",
          String(Math.round(d.bfShareDeltaKr)),
          pctField(d.rate_pct),
          String(Math.round(d.provisionDeltaKr)),
        ].join(";"),
      );
    }
    for (const [userId, net] of deltas.netByUser) {
      const name = deltas.rows.find((r) => r.user_id === userId)?.full_name ?? userId;
      lines.push([csvField(`Netto neste kjøring ${name}`), "", "", "", "", "", "", "", "", String(Math.round(net))].join(";"));
    }
  }

  return lines.join("\n");
}

// ── Lønnsgrunnlag (VIB-1224) ───────────────────────────────────────────

const PAYROLL_HEADER =
  "Ansatt;Dato;Prosjekt;Oppgave;Type;Helg-/helligdagstimer;Overnattinger";

/**
 * Lønnsgrunnlag som CSV: én grunnlagsrad per føring (dato + type), sum-rad
 * per ansatt og totalsum. INGEN kronekolonner (Erik 15.07) — lønn beregner
 * satsene selv. Semikolon/BOM-mønsteret som provisjonseksporten.
 */
export function payrollBasisToCsv(employees: PayrollBasisEmployee[]): string {
  const hoursField = (n: number) => String(Math.round(n * 100) / 100).replace(".", ",");
  const lines = [PAYROLL_HEADER];
  let totalHours = 0;
  let totalNights = 0;

  for (const emp of employees) {
    for (const l of emp.lines) {
      lines.push(
        [
          csvField(emp.full_name),
          l.date,
          csvField(l.project_name),
          csvField(l.task_description),
          l.day_kind === "weekday" ? "—" : DAY_KIND_NB[l.day_kind],
          hoursField(l.weekend_holiday_hours),
          String(l.overnight_count),
        ].join(";"),
      );
    }
    lines.push(
      [
        csvField(`Sum ${emp.full_name}`),
        "",
        "",
        `${emp.lines.length} føringer`,
        "",
        hoursField(emp.total_weekend_holiday_hours),
        String(emp.total_overnights),
      ].join(";"),
    );
    totalHours += emp.total_weekend_holiday_hours;
    totalNights += emp.total_overnights;
  }

  lines.push(["Totalsum", "", "", "", "", hoursField(totalHours), String(totalNights)].join(";"));
  return lines.join("\n");
}

// ── Statistlønn (VIB-1283) ─────────────────────────────────────────────

const EXTRAS_SALARY_HEADER = "Navn;Personnummer;Kontonummer;Prosjektkode;Prosjekt;Beløp";

/**
 * Statistliste som CSV (VIB-1283, lønnsdelen): én rad per person × prosjekt,
 * sum-rad per person og totalsum. Kun eksplisitte lønnsmottakere
 * (payment_type='lonnet') er allerede filtrert inn av buildExtrasSalaryExport
 * før dette kalles — faktura-mottakere og uavklart/tom/NULL status er ute.
 * Semikolon/BOM-mønsteret som provisjons-/lønnsgrunnlagseksporten.
 *
 * Kolonnesett er et forslag basert på hus-formatet og bølge 8-spec-teksten
 * ("personnr, kontonr, beløp per person") — IKKE timer/sats, siden statister
 * ikke timeføres og ikke har noe satsfelt i datamodellen (se
 * src/lib/extras-salary-calc.ts for verifiseringen). Erik bekrefter/justerer
 * kolonner + rekkefølge ved review.
 */
export function extrasSalaryToCsv(entries: ExtrasSalaryEntry[]): string {
  const lines = [EXTRAS_SALARY_HEADER];

  const byName = new Map<string, ExtrasSalaryEntry[]>();
  for (const e of entries) {
    const key = e.full_name;
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key)!.push(e);
  }

  let grandTotal = 0;
  for (const [name, rows] of byName) {
    let sum = 0;
    for (const r of rows) {
      sum += r.agreed_amount;
      lines.push(
        [
          csvField(name),
          csvField(r.personal_number ?? ""),
          csvField(r.bank_account ?? ""),
          csvField(r.project_code ?? ""),
          csvField(r.project_name),
          String(r.agreed_amount),
        ].join(";"),
      );
    }
    lines.push(
      [
        csvField(`Sum ${name}`),
        "",
        "",
        "",
        `${rows.length} prosjekt${rows.length === 1 ? "" : "er"}`,
        String(sum),
      ].join(";"),
    );
    grandTotal += sum;
  }

  lines.push(["Totalsum", "", "", "", "", String(grandTotal)].join(";"));
  return lines.join("\n");
}

const EXTRAS_PAYMENT_QUEUE_HEADER =
  "Navn;Rolle;Prosjektkode;Prosjekt;Prosjektleder;Betalingstype;Personnummer;Kontonummer;Meldt klar;Beløp";

/** Én rad i utbetalingslista, slik CSV-en trenger den (VIB-2092). */
export interface ExtrasPaymentQueueRow {
  full_name: string;
  role: string | null;
  project_code: string | null;
  project_name: string;
  production_lead_name: string | null;
  payment_type: string | null;
  personal_number: string | null;
  bank_account: string | null;
  ready_for_payment_at: string | null;
  agreed_amount: number;
}

/**
 * Utbetalingslista som CSV (VIB-2092): det DL laster ned for å gjøre
 * utbetalingen.
 *
 * BEVISST noe annet enn `extrasSalaryToCsv`. Den er en lønnsfil over ALLE
 * statister med bekreftet `payment_type='lonnet'`, uavhengig av om noen har
 * meldt dem klare. Denne er arbeidslista: kun de en prosjektleder faktisk har
 * meldt klare, uansett betalingstype, med prosjektleder og dato for
 * klarmeldingen slik at DL kan følge opp den som meldte.
 *
 * Betalingstype er med som kolonne nettopp fordi lista blander lønn og faktura
 * — uten den ville DL ikke sett hvilke rader som skal gjennom lønnskjøringen.
 */
export function extrasPaymentQueueToCsv(rows: ExtrasPaymentQueueRow[]): string {
  const lines = [EXTRAS_PAYMENT_QUEUE_HEADER];
  let total = 0;

  for (const r of rows) {
    total += r.agreed_amount;
    lines.push(
      [
        csvField(r.full_name),
        csvField(r.role ?? ""),
        csvField(r.project_code ?? ""),
        csvField(r.project_name),
        csvField(r.production_lead_name ?? ""),
        csvField(r.payment_type ?? ""),
        csvField(r.personal_number ?? ""),
        csvField(r.bank_account ?? ""),
        csvField(r.ready_for_payment_at?.slice(0, 10) ?? ""),
        String(r.agreed_amount),
      ].join(";"),
    );
  }

  lines.push(
    ["Totalsum", "", "", "", "", "", "", "", "", String(total)].join(";"),
  );
  return lines.join("\n");
}

/**
 * Statistlista for ETT prosjekt som CSV (VIB-2398). Speiler kolonnene i
 * «Print liste» (src/pages/StatisterPrintPage.tsx) — samme rader, samme
 * felter, bare maskinlesbart. Dette er BEVISST noe annet enn
 * `extrasSalaryToCsv`: den er en lønnsfil på tvers av prosjekter, filtrert
 * til `payment_type='lonnet'`. Denne tar alle statister på prosjektet, uten
 * filtrering, slik lista og utskriften viser dem.
 *
 * Dato er ISO (YYYY-MM-DD) og ikke norsk visningsformat: CSV-en leses av
 * regneark og videre systemer, der ISO sorterer og parses riktig.
 *
 * Strukturell input-type (ikke `ProjectExtra` fra hooks) så lib ikke kobles
 * til hooks-laget — samme mønster som `OfferLineShape` i editable-line.ts.
 */
export interface ExtraListRow {
  full_name: string | null;
  name: string | null;
  role: string | null;
  phone_number: string | null;
  email: string | null;
  address: string | null;
  personal_number: string | null;
  bank_account: string | null;
  agreed_amount: number | null;
  created_at: string;
}

const EXTRAS_LIST_HEADER =
  "Navn;Rolle;Telefon;E-post;Adresse;Personnr;Kontonr;Beløp;Registrert";

export function extrasListToCsv(rows: ExtraListRow[]): string {
  const lines = [EXTRAS_LIST_HEADER];
  for (const r of rows) {
    lines.push(
      [
        csvField(r.full_name ?? r.name ?? ""),
        csvField(r.role ?? ""),
        csvField(r.phone_number ?? ""),
        csvField(r.email ?? ""),
        csvField(r.address ?? ""),
        // Personnr/kontonr er tallstrenger med ledende nuller — quotes bevarer
        // dem ikke i Excel, men csvField hindrer i det minste formel-tolkning.
        csvField(r.personal_number ?? ""),
        csvField(r.bank_account ?? ""),
        String(Math.round(r.agreed_amount ?? 0)),
        csvField((r.created_at ?? "").slice(0, 10)),
      ].join(";"),
    );
  }
  return lines.join("\n");
}

/** Trigger nedlasting i nettleser. BOM for at Excel leser UTF-8. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
